import './index.css'
import { ExportOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { CIMSLinkProps } from './types'
import { Popover, Tooltip, Typography } from 'antd'

/**
 * Renders either a React Router {@link Link} using `to` or a native anchor
 * using `href`. Both kinds support `newTab`; `external` selects the native
 * anchor rather than controlling the browsing context.
 *
 * For most usages of internal links, use {@link ProjectLink}, which wraps this
 * component.
 *
 * @param props {@link CIMSLinkProps}
 */
const CIMSLink: React.FC<CIMSLinkProps> = ({
  children,
  className,
  newTab: requestedNewTab,
  tooltip,
  ...props
}) => {
  const newTab = requestedNewTab ?? props.external === true
  const content = (
    <>
      {children}
      {newTab && (
        <ExportOutlined aria-hidden="true" className="cims-link-new-tab-icon" />
      )}
    </>
  )
  const newTabProps = newTab
    ? {
        rel: 'noopener noreferrer',
        target: '_blank',
      }
    : {}
  let link
  if (props.external === true) {
    const { href } = props
    if (!href) link = <Typography.Text disabled>{children}</Typography.Text>
    else
      link = (
        <a className={className} href={href} {...newTabProps}>
          {content}
        </a>
      )
  } else {
    /**
     * for some reason, upgrading to the latest 17 version of react, react-dom,
     * and the latest version 5 of react-router-dom makes it so Link's definition
     * is no longer a JSX.Element...
     *
     * since the definitions are from definitely typed and not react-router-dom...
     * i'm going to ignore this since it works...
     */
    link = (
      // @ts-expect-error
      <Link className={className} to={props.to} {...newTabProps}>
        {content}
      </Link>
    )
  }
  if (typeof tooltip === 'string')
    return <Tooltip title={tooltip}>{link}</Tooltip>
  else if (tooltip) return <Popover {...tooltip}>{link}</Popover>
  return link
}

export default CIMSLink
